'use client';

import useSWRImmutable from 'swr/immutable';
import {isError} from '@epic-mw/error-types';
import useSWRMutation from 'swr/mutation';
import {useLocale} from '@epic-mw/i18n';
import {useCallback} from 'react';
import {debounce} from 'lodash';
import {CatalogOfferProduct} from '@/types';
import {useCountry} from '../geo';
import {CurrencyRes} from './types';
import {
	getCurrencyDetail,
	getCatalogOffer,
	getCatalogOffers,
	getCatalogCountry,
	getCatalogOfferItems,
	getAgeRating,
	searchStore
} from './catalog.fetch';

export const useCurrencyDetail = (
	currency: string
): {
	currency?: CurrencyRes;
	isLoading: boolean;
	error?: string;
} => {
	const shouldFetch = !!currency;

	const {data, error} = useSWRImmutable(shouldFetch ? currency : null, getCurrencyDetail, {
		errorRetryCount: 1
	});

	return {
		currency: data,
		isLoading: !data && !error,
		error: isError(error) ? error.message : error
	};
};

export const useCatalogOffer = (
	sandboxId: string,
	offerId: string,
	locale: string,
	country: string
) => {
	const shouldFetch = locale && country && sandboxId && offerId;
	const {data, isLoading, error} = useSWRImmutable(
		shouldFetch ? [sandboxId, offerId, locale, country] : null,
		(_) => getCatalogOffer(sandboxId, offerId, locale, country),
		{
			errorRetryCount: 1
		}
	);

	return {
		data,
		isLoading,
		error
	};
};

export const useCatalogOffers = (
	products: CatalogOfferProduct[],
	locale: string,
	country: string
) => {
	const productStr = products?.length ? JSON.stringify(products) : false;
	const shouldFetch = locale && country && !!productStr;
	const {data, isLoading, error} = useSWRImmutable(
		shouldFetch ? ['offers', productStr, locale, country] : null,
		(_) => getCatalogOffers(products, locale, country),
		{
			errorRetryCount: 1
		}
	);

	return {
		data: data?.filter(Boolean),
		isLoading,
		error
	};
};

interface UseDispatchOptions {
	onSuccess?: () => void;
	onError?: (error: string) => void;
}

export const useSearchStoreDispatch = (cacheKey: string, options?: UseDispatchOptions) => {
	const locale = useLocale();

	const handler = useCallback(
		async (url: string, {arg}: {arg: {sandboxId: string; country: string}}) => {
			if (!arg.sandboxId || !arg.country) {
				console.warn('No sandboxId or country provided...', arg.sandboxId, arg.country);
				return undefined;
			}
			const response = await searchStore({
				allowCountries: arg.country,
				category: 'addons|digitalextras',
				count: 40,
				country: arg.country,
				locale,
				namespace: arg.sandboxId,
				sortBy: 'relevancy,viewableDate',
				sortDir: 'DESC,DESC',
				withPrice: true
			});

			return response?.elements || [];
		},
		[locale]
	);

	const {trigger, ...query} = useSWRMutation(cacheKey, handler, {
		onError: (error: string, _key, _config) => {
			options?.onError?.(error);
			return error;
		},
		onSuccess: (_result, _key, _config) => {
			options?.onSuccess?.();
		},
		populateCache: true
	});

	return {
		...query,
		trigger,
		reset: useCallback(() => debounce(query.reset, 700), [query.reset])
	};
};

export const useCatalogOffersDispatch = (cacheKey: string, options?: UseDispatchOptions) => {
	const locale = useLocale();

	const handler = useCallback(
		async (url: string, {arg}: {arg: {products: CatalogOfferProduct[]; country: string}}) => {
			if (!arg.products || !arg.country) {
				console.warn('No products or country provided...', arg.products, arg.country);
				return undefined;
			}
			return getCatalogOffers(arg.products, locale, arg.country);
		},
		[locale]
	);

	const {trigger, ...query} = useSWRMutation(cacheKey, handler, {
		onError: (error: string, _key, _config) => {
			options?.onError?.(error);
			return error;
		},
		onSuccess: (_result, _key, _config) => {
			options?.onSuccess?.();
		},
		populateCache: true
	});

	return {
		...query,
		trigger,
		reset: useCallback(() => debounce(query.reset, 700), [query.reset])
	};
};

export const useCatalogOfferItems = (sandboxId: string, offerId: string) => {
	const locale = useLocale();
	const {data, isLoading, error} = useSWRImmutable(
		[sandboxId, offerId, locale],
		(_) => getCatalogOfferItems(sandboxId, offerId, locale),
		{
			errorRetryCount: 1
		}
	);
	return {data, isLoading: isLoading || (!data && !error), error};
};

export const useCatalogCountry = () => {
	const countryCode = useCountry();
	return useSWRImmutable(countryCode, getCatalogCountry, {
		errorRetryCount: 1
	});
};

export const useOfferAgeRating = () => {
	const countryCode = useCountry();
	const locale = useLocale();
	const {data, isLoading, error} = useSWRImmutable(
		[locale, countryCode],
		(_) => getAgeRating(locale, countryCode),
		{
			errorRetryCount: 1
		}
	);

	return {
		data: data?.data,
		isLoading,
		error
	};
};
